import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { apiClient, User } from "@rti/shared";
import { useAuth } from "@/contexts/AuthContext";
import {
  Building2,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  DownloadIcon,
  Eye,
  EyeOff,
  Filter,
  History,
  KeyRound,
  Mail,
  Plus,
  SaveIcon,
  Search,
  Trash2,
  UserRoundPen,
  Users,
  X,
} from "lucide-react";

const EMPLOYEE_MEMBERSHIP_ID = 4;

const UserManagement: React.FC = () => {
  const { user, realUser, startImpersonation } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [memberships, setMemberships] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Company filter state (multi-select dropdown)
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [companyFilterSearch, setCompanyFilterSearch] = useState('');
  const companyDropdownRef = useRef<HTMLDivElement>(null);

  const [selectedMembershipIds, setSelectedMembershipIds] = useState<number[]>([]);
  const [membershipDropdownOpen, setMembershipDropdownOpen] = useState(false);
  const membershipDropdownRef = useRef<HTMLDivElement>(null);

  const [trialFilter, setTrialFilter] = useState<'all' | 'trial' | 'not_trial'>('all');
  const [trialDropdownOpen, setTrialDropdownOpen] = useState(false);
  const trialDropdownRef = useRef<HTMLDivElement>(null);

  const [collapsedCompanyIds, setCollapsedCompanyIds] = useState<Set<number>>(new Set());

  // Add/Edit User Modal logic
  const [userForm, setUserForm] = useState<any>({});
  const [modalTab, setModalTab] = useState(1);
  const [modalError, setModalError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Password change modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  const [passwordConfirmValue, setPasswordConfirmValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showPasswordConfirmDialog, setShowPasswordConfirmDialog] = useState(false);

  // Password reset email modal states
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const [resetPasswordSending, setResetPasswordSending] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);

  // Access control: Only admins can access
  useEffect(() => {
    if (!user?.is_admin) {
      window.location.href = '/dashboard';
      return;
    }
    Promise.all([
      apiClient.getAllUsers(),
      apiClient.fetchMemberships(),
      apiClient.adminListCompanies(),
    ])
      .then(([users, memberships, companies]) => {
        setUsers(users);
        setMemberships(memberships);
        setCompanies(companies);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [user]);

  const getCompany = (u: User) => companies.find(c => c.company_id === u.company_id);
  const getCompanyName = (u: User) => getCompany(u)?.name ?? u.company_name ?? '';
  const getInheritedMembershipId = (u: User) => getCompany(u)?.membership_id ?? u.effective_membership_id;
  const getMembershipName = (membershipId?: number) => {
    const m = memberships.find(m => m.membership_id === membershipId);
    return m?.name ? m.name.charAt(0).toUpperCase() + m.name.slice(1).toLowerCase() : '';
  };
  const isEmployee = (u: User) => getInheritedMembershipId(u) === EMPLOYEE_MEMBERSHIP_ID;

  // Company list with user counts, sorted by name
  const companiesWithCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    users.forEach(u => {
      if (u.company_id) counts[u.company_id] = (counts[u.company_id] || 0) + 1;
    });
    return companies
      .map(c => ({ ...c, userCount: counts[c.company_id] || 0 }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [companies, users]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target as Node)) {
        setCompanyDropdownOpen(false);
      }
      if (membershipDropdownRef.current && !membershipDropdownRef.current.contains(e.target as Node)) {
        setMembershipDropdownOpen(false);
      }
      if (trialDropdownRef.current && !trialDropdownRef.current.contains(e.target as Node)) {
        setTrialDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<'name' | 'email' | 'created' | 'last_login' | 'role'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [userSearch, setUserSearch] = useState('');

  // Users filtered by selected company and search
  const companyUsers = useMemo(() => {
    let filtered = selectedCompanyIds.length > 0
      ? users.filter(u => selectedCompanyIds.includes(u.company_id as number))
      : users;
    if (userSearch.trim()) {
      const terms = userSearch.toLowerCase().trim().split(/\s+/);
      filtered = filtered.filter(u => {
        const haystack = `${u.first_name} ${u.last_name} ${u.email} ${getCompanyName(u)}`.toLowerCase();
        return terms.every(term => haystack.includes(term));
      });
    }
    if (selectedMembershipIds.length > 0) {
      filtered = filtered.filter(u => selectedMembershipIds.includes(getInheritedMembershipId(u) as number));
    }
    if (trialFilter === 'trial') {
      filtered = filtered.filter(u => !!u.on_free_trial);
    } else if (trialFilter === 'not_trial') {
      filtered = filtered.filter(u => !u.on_free_trial);
    }
    return [...filtered].sort((a, b) => {
      if (sortColumn === 'name' && selectedCompanyIds.length !== 1) {
        const aComp = (getCompany(a)?.name ?? a.company_name ?? '').toLowerCase();
        const bComp = (getCompany(b)?.name ?? b.company_name ?? '').toLowerCase();
        if (aComp !== bComp) return aComp.localeCompare(bComp);
      }
      let aValue: string | number | boolean;
      let bValue: string | number | boolean;
      switch (sortColumn) {
        case 'name':
          aValue = (a.first_name + a.last_name).toLowerCase();
          bValue = (b.first_name + b.last_name).toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'created':
          aValue = a.created_at ? (typeof a.created_at === 'number' ? a.created_at : Date.parse(a.created_at as string) || 0) : 0;
          bValue = b.created_at ? (typeof b.created_at === 'number' ? b.created_at : Date.parse(b.created_at as string) || 0) : 0;
          break;
        case 'last_login': {
          const getCompanyMaxLogin = (u: User) => {
            const companyUsers = users.filter(cu => cu.company_id === u.company_id);
            return companyUsers.reduce<number>((max, cu) => {
              const v = cu.last_login ? (typeof cu.last_login === 'number' ? cu.last_login : Date.parse(cu.last_login as string) / 1000) : 0;
              return v > max ? v : max;
            }, 0);
          };
          aValue = selectedCompanyIds.length === 1
            ? (a.last_login ? (typeof a.last_login === 'number' ? a.last_login : Date.parse(a.last_login as string) || 0) : 0)
            : getCompanyMaxLogin(a);
          bValue = selectedCompanyIds.length === 1
            ? (b.last_login ? (typeof b.last_login === 'number' ? b.last_login : Date.parse(b.last_login as string) || 0) : 0)
            : getCompanyMaxLogin(b);
          break;
        }
        case 'role':
          aValue = (a as any).company_role || 'member';
          bValue = (b as any).company_role || 'member';
          break;
        default:
          aValue = '';
          bValue = '';
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, selectedCompanyIds, userSearch, selectedMembershipIds, trialFilter, sortColumn, sortDirection]);

  // Pagination
  const USERS_PER_PAGE = 35;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(companyUsers.length / USERS_PER_PAGE);
  const paginatedUsers = companyUsers.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE);

  const visibleCompanyIds = useMemo(() => Array.from(new Set(companyUsers.map(u => u.company_id as number))), [companyUsers]);
  const allCompaniesCollapsed = visibleCompanyIds.length > 0 && visibleCompanyIds.every(id => collapsedCompanyIds.has(id));
  const toggleAllCompanies = () => {
    setCollapsedCompanyIds(prev => {
      const next = new Set(prev);
      if (allCompaniesCollapsed) {
        visibleCompanyIds.forEach(id => next.delete(id));
      } else {
        visibleCompanyIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  // Reset page when company or search changes
  useEffect(() => { setCurrentPage(1); }, [selectedCompanyIds, userSearch, selectedMembershipIds, trialFilter]);

  const handleColumnSort = (column: typeof sortColumn) => {
    if (sortColumn === column) {
      setSortDirection(dir => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Open Add/Edit modal
  const openAddUser = () => {
    setEditingUser(null);
    setUserForm({
      first_name: '',
      last_name: '',
      email: '',
      is_admin: false,
      is_active: true,
      company_id: undefined,
    });
    setModalTab(1);
    setShowAddEditModal(true);
    setModalError(null);
  };

  const openEditUser = async (user: User) => {
    setEditingUser(user);
    setUserForm({ ...user, password: '' });
    setModalTab(1);
    setShowAddEditModal(true);
    setModalError(null);
    setSaving(false);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setUserForm((prev: any) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveUser = async () => {
    setSaving(true);
    setModalError(null);
    try {
      if (!userForm.first_name || !userForm.last_name || !userForm.email) {
        setModalError('All fields must be filled!');
        setSaving(false);
        return;
      }
      if (editingUser) {
        await apiClient.updateUser(editingUser.user_id, userForm);
      } else {
        await apiClient.createUser(userForm);
      }
      setShowAddEditModal(false);
      const users = await apiClient.getAllUsers();
      setUsers(users);
    } catch (e: any) {
      setModalError(e?.response?.detail || e.message);
    }
    setSaving(false);
  };

  // Password modal handlers
  const openPasswordModal = () => {
    setPasswordValue('');
    setPasswordConfirmValue('');
    setShowPassword(false);
    setShowPasswordConfirm(false);
    setPasswordError(null);
    setShowPasswordModal(true);
    setShowPasswordConfirmDialog(false);
  };

  const validateAndConfirmPasswordChange = () => {
    setPasswordError(null);
    if (!passwordValue) { setPasswordError('Password cannot be empty'); return; }
    if (passwordValue !== passwordConfirmValue) { setPasswordError('Passwords do not match'); return; }
    if (passwordValue.length < 8) { setPasswordError('Password must be at least 8 characters long'); return; }
    setShowPasswordConfirmDialog(true);
  };

  const handlePasswordChange = async () => {
    setPasswordSaving(true);
    setPasswordError(null);
    try {
      if (!editingUser) { setPasswordError('No user selected'); setPasswordSaving(false); return; }
      await apiClient.adminChangeUserPassword(editingUser.user_id, passwordValue);
      setShowPasswordConfirmDialog(false);
      setShowPasswordModal(false);
      setPasswordValue('');
      setPasswordConfirmValue('');
    } catch (e: any) {
      setPasswordError(e?.response?.detail || e.message);
      setShowPasswordConfirmDialog(false);
    }
    setPasswordSaving(false);
  };

  const openResetPasswordModal = () => {
    setResetPasswordError(null);
    setResetPasswordSuccess(false);
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async () => {
    setResetPasswordSending(true);
    setResetPasswordError(null);
    setResetPasswordSuccess(false);
    try {
      if (!editingUser?.email) { setResetPasswordError('No user selected'); setResetPasswordSending(false); return; }
      await apiClient.requestPasswordReset(editingUser.email);
      setResetPasswordSuccess(true);
    } catch (e: any) {
      setResetPasswordError(e?.response?.detail || e.message);
    }
    setResetPasswordSending(false);
  };

  const handleDeleteUser = async (user: User) => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiClient.deleteUser(user.user_id);
      setShowDeleteModal(false);
      const users = await apiClient.getAllUsers();
      setUsers(users);
    } catch (e: any) {
      setDeleteError(e?.response?.detail || e.message);
    }
    setDeleting(false);
  };

  const exportUsersToExcel = () => {
    if (!users.length) return;
    const formatDate = (value?: number | string | null) => {
      if (!value) return '';
      const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
      return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
    };
    const formatDateTime = (value?: number | string | null) => {
      if (!value) return '';
      const date = typeof value === 'number' ? new Date(value * 1000) : new Date(value);
      return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
    };
    const exportList = selectedCompanyIds.length > 0 ? companyUsers : users;
    const rows = exportList.map(u => ({
      "First Name": u.first_name,
      "Last Name": u.last_name,
      "Email": u.email,
      "Company": getCompanyName(u),
      "Role": (u as any).company_role || 'member',
      "Last Login": formatDateTime(u.last_login as unknown as number | string | null),
      "Active Status": u.is_active ? "Active" : "Deactivated",
      "Membership": getMembershipName(getInheritedMembershipId(u)),
      "Free Trial": u.on_free_trial ? "Yes" : "No",
      "Trial Started": formatDate(u.free_trial_started_at as unknown as number | string | null),
      "Trial Duration (days)": u.free_trial_duration_days ?? "",
      "Created": formatDate(u.created_at as unknown as number | string | null),
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `ResinSmart-Users-${stamp}.xlsx`);
  };

  // Details modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsUser, setDetailsUser] = useState<User | null>(null);

  const navigateToUserActivity = (email: string) => {
    sessionStorage.setItem('userManagementState', JSON.stringify({ selectedCompanyIds, userSearch, currentPage, sortColumn, sortDirection, selectedMembershipIds, trialFilter }));
    window.location.href = `/user-activity/${encodeURIComponent(email)}`;
  };

  // Restore state from sessionStorage on mount
  useEffect(() => {
    const savedState = sessionStorage.getItem('userManagementState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        if (state.userSearch) setUserSearch(state.userSearch);
        if (state.currentPage) setCurrentPage(state.currentPage);
        if (state.sortColumn) setSortColumn(state.sortColumn);
        if (state.sortDirection) setSortDirection(state.sortDirection);
        if (state.selectedCompanyIds) setSelectedCompanyIds(state.selectedCompanyIds);
        if (state.selectedMembershipIds) setSelectedMembershipIds(state.selectedMembershipIds);
        if (state.trialFilter) setTrialFilter(state.trialFilter);
        sessionStorage.removeItem('userManagementState');
      } catch {}
    }
  }, []);

  const SortIcon = ({ col }: { col: typeof sortColumn }) =>
    sortColumn === col
      ? sortDirection === 'asc' ? <ChevronUp className="inline w-4 h-4 ml-1" /> : <ChevronDown className="inline w-4 h-4 ml-1" />
      : <ChevronsUpDown className="inline w-4 h-4 ml-1 opacity-50" />;

  return (
    <div className="bg-rs-off-black text-rs-off-white min-h-screen p-8">
      <div className="content-toolbar mb-5">
        <button className="jet" onClick={() => window.location.href = '/dashboard'}>
          <span className="material-symbols-outlined">arrow_back_ios</span>
          Back to Landing Page
        </button>
      </div>
      <h1 className="text-3xl font-light mb-6"><Users className="inline text-rs-dark-green mr-2" size={45} /> User Management</h1>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="flex flex-col h-[calc(100vh-180px)]">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="flex items-center gap-2 mr-auto">
              <Users className="text-rs-dark-green hidden sm:block" size={20} />
              <span className="text-sm text-rs-off-white/50">{companyUsers.length} user{companyUsers.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-rs-off-white/40" size={14} />
              <input
                className="pl-7 pr-3 py-2 rounded border border-rs-jet-light bg-rs-jet text-rs-off-white text-sm w-48 focus:outline-none focus:border-rs-green"
                placeholder="Search users..."
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
            </div>
            <div className="relative" ref={companyDropdownRef}>
              <button
                onClick={() => setCompanyDropdownOpen(o => !o)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded border text-sm ${selectedCompanyIds.length > 0 ? 'border-rs-green text-rs-green bg-rs-green/10' : 'border-rs-jet-light bg-rs-jet text-rs-off-white'}`}
              >
                <Filter size={14} />
                {selectedCompanyIds.length > 0 ? `Company (${selectedCompanyIds.length})` : 'Company'}
                {selectedCompanyIds.length > 0
                  ? <X size={12} className="ml-1" onClick={e => { e.stopPropagation(); setSelectedCompanyIds([]); }} />
                  : <ChevronDown size={14} />
                }
              </button>
              {companyDropdownOpen && (
                <div className="absolute top-full mt-1 left-0 z-30 w-64 bg-rs-off-black border border-rs-jet-light rounded shadow-xl max-h-80 overflow-y-auto">
                  <div className="px-3 py-2 border-b border-rs-jet sticky top-0 bg-rs-off-black">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-rs-off-white/50">{companiesWithCounts.length} companies</span>
                      {selectedCompanyIds.length > 0 && (
                        <button className="text-xs text-rs-green hover:text-rs-green/70" onClick={() => setSelectedCompanyIds([])}>Clear all</button>
                      )}
                    </div>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-rs-off-white/40" size={12} />
                      <input
                        className="w-full pl-6 pr-2 py-1 rounded border border-rs-jet-light bg-rs-jet text-rs-off-white text-xs focus:outline-none focus:border-rs-green"
                        placeholder="Search companies..."
                        value={companyFilterSearch}
                        onChange={e => setCompanyFilterSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  {(() => {
                    const q = companyFilterSearch.trim().toLowerCase();
                    const matching = q ? companiesWithCounts.filter(c => c.name.toLowerCase().includes(q)) : companiesWithCounts;
                    const sorted = [...matching].sort((a, b) => {
                      const aSelected = selectedCompanyIds.includes(a.company_id);
                      const bSelected = selectedCompanyIds.includes(b.company_id);
                      if (aSelected !== bSelected) return aSelected ? -1 : 1;
                      return a.name.localeCompare(b.name);
                    });
                    const firstUnselectedIdx = selectedCompanyIds.length > 0
                      ? sorted.findIndex(c => !selectedCompanyIds.includes(c.company_id))
                      : -1;
                    if (sorted.length === 0) {
                      return <div className="px-3 py-4 text-center text-xs text-rs-off-white/40">No companies match</div>;
                    }
                    return sorted.map((c, idx) => (
                      <React.Fragment key={c.company_id}>
                        {idx === firstUnselectedIdx && (
                          <div className="border-t border-rs-jet-light/50 my-1" />
                        )}
                        <label className="flex items-center gap-2 px-3 py-2 hover:bg-rs-jet/50 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={selectedCompanyIds.includes(c.company_id)}
                            onChange={e => setSelectedCompanyIds(prev =>
                              e.target.checked ? [...prev, c.company_id] : prev.filter((id: number) => id !== c.company_id)
                            )}
                            className="h-3.5 w-3.5"
                          />
                          <span className="flex-1 truncate">{c.name}</span>
                          <span className="text-xs text-rs-off-white/40">{c.userCount}</span>
                        </label>
                      </React.Fragment>
                    ));
                  })()}
                </div>
              )}
            </div>
            <div className="relative" ref={membershipDropdownRef}>
              <button
                onClick={() => setMembershipDropdownOpen(o => !o)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded border text-sm ${selectedMembershipIds.length > 0 ? 'border-rs-green text-rs-green bg-rs-green/10' : 'border-rs-jet-light bg-rs-jet text-rs-off-white'}`}
              >
                <Filter size={14} />
                {selectedMembershipIds.length > 0 ? `Membership (${selectedMembershipIds.length})` : 'Membership'}
                {selectedMembershipIds.length > 0
                  ? <X size={12} className="ml-1" onClick={e => { e.stopPropagation(); setSelectedMembershipIds([]); }} />
                  : <ChevronDown size={14} />
                }
              </button>
              {membershipDropdownOpen && (
                <div className="absolute top-full mt-1 left-0 z-30 w-48 bg-rs-off-black border border-rs-jet-light rounded shadow-xl max-h-72 overflow-y-auto">
                  <div className="px-3 py-2 border-b border-rs-jet flex items-center justify-between sticky top-0 bg-rs-off-black">
                    <span className="text-xs text-rs-off-white/50">{memberships.length} tiers</span>
                    {selectedMembershipIds.length > 0 && (
                      <button className="text-xs text-rs-green hover:text-rs-green/70" onClick={() => setSelectedMembershipIds([])}>Clear all</button>
                    )}
                  </div>
                  {[...memberships].sort((a, b) => (a.membership_id ?? 0) - (b.membership_id ?? 0)).map(m => (
                    <label key={m.membership_id} className="flex items-center gap-2 px-3 py-2 hover:bg-rs-jet/50 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={selectedMembershipIds.includes(m.membership_id)}
                        onChange={e => setSelectedMembershipIds(prev =>
                          e.target.checked ? [...prev, m.membership_id] : prev.filter((id: number) => id !== m.membership_id)
                        )}
                        className="h-3.5 w-3.5"
                      />
                      <span className="flex-1 truncate">{getMembershipName(m.membership_id)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="relative" ref={trialDropdownRef}>
              <button
                onClick={() => setTrialDropdownOpen(o => !o)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded border text-sm ${trialFilter !== 'all' ? 'border-rs-green text-rs-green bg-rs-green/10' : 'border-rs-jet-light bg-rs-jet text-rs-off-white'}`}
              >
                <Filter size={14} />
                {trialFilter === 'trial' ? 'Trial: Yes' : trialFilter === 'not_trial' ? 'Trial: No' : 'Trial'}
                {trialFilter !== 'all'
                  ? <X size={12} className="ml-1" onClick={e => { e.stopPropagation(); setTrialFilter('all'); }} />
                  : <ChevronDown size={14} />
                }
              </button>
              {trialDropdownOpen && (
                <div className="absolute top-full mt-1 left-0 z-30 w-40 bg-rs-off-black border border-rs-jet-light rounded shadow-xl">
                  {([['all', 'All'], ['trial', 'On Trial'], ['not_trial', 'Not on Trial']] as const).map(([value, label]) => (
                    <button
                      key={value}
                      onClick={() => { setTrialFilter(value); setTrialDropdownOpen(false); }}
                      className={`block w-full text-left px-3 py-2 hover:bg-rs-jet/50 text-sm ${trialFilter === value ? 'text-rs-green' : 'text-rs-off-white'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button className="flex items-center gap-1.5 px-3 py-2 rounded border border-rs-jet-light bg-rs-jet text-rs-off-white text-sm" onClick={toggleAllCompanies}>
              <ChevronsUpDown size={14} /> {allCompaniesCollapsed ? 'Expand all' : 'Collapse all'}
            </button>
            <button className="green px-3 py-2 rounded text-sm" onClick={openAddUser}><Plus className="inline h-4 mr-1" /> Add User</button>
            <button className="green px-3 py-2 rounded text-sm" onClick={exportUsersToExcel}><DownloadIcon className="inline h-4 mr-1" /> Export</button>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto rounded-lg border border-rs-jet">
              <table className="min-w-full bg-rs-off-black text-rs-off-white text-sm">
                <thead className="sticky top-0 bg-rs-off-black z-10">
                  <tr>
                    <th className="cursor-pointer select-none px-3 py-2 text-left" onClick={() => handleColumnSort('name')}>
                      Company Name <SortIcon col="name" />
                    </th>
                    <th className="cursor-pointer select-none px-3 py-2 text-left hidden md:table-cell" onClick={() => handleColumnSort('email')}>
                      Email <SortIcon col="email" />
                    </th>
                    <th className="cursor-pointer select-none px-3 py-2 text-center hidden lg:table-cell" onClick={() => handleColumnSort('last_login')}>
                      Last Login <SortIcon col="last_login" />
                    </th>
                    <th className="px-3 py-2 text-center hidden md:table-cell">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let lastCompanyId: number | null | undefined = undefined;
                    return paginatedUsers.map(u => {
                      const isNewCompany = selectedCompanyIds.length !== 1 && u.company_id !== lastCompanyId;
                      if (isNewCompany) lastCompanyId = u.company_id as number;
                      const actionButtons = (
                        <>
                          <button className="bg-rs-jet-light/70 hover:bg-rs-jet-light text-xs px-2 py-1 rounded" onClick={() => navigateToUserActivity(u.email)} title="View History">
                            <History className="inline" size={14} />
                          </button>
                          <button className="green text-xs px-2 py-1 rounded" onClick={() => openEditUser(u)} title="Edit">
                            <UserRoundPen className="inline" size={14} />
                          </button>
                          <button className="bg-rs-blue-dash/70 hover:bg-rs-blue-dash text-xs px-2 py-1 rounded" onClick={() => { setEditingUser(u); openResetPasswordModal(); }} title="Send Reset Email">
                            <Mail className="inline" size={14} />
                          </button>
                          <button className="bg-amber-600/80 hover:bg-amber-500 text-xs px-2 py-1 rounded" onClick={() => { setEditingUser(u); openPasswordModal(); }} title="Set New Password">
                            <KeyRound className="inline" size={14} />
                          </button>
                          <button
                            className={`text-xs px-2 py-1 rounded ${u.user_id === realUser?.user_id ? 'bg-gray-400 opacity-50 cursor-not-allowed' : ' text-rs-off-black bg-amber-400 hover:bg-amber-400/70'}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (u.user_id === realUser?.user_id) return;
                              try {
                                const target = await apiClient.impersonateUser(u.user_id);
                                startImpersonation(target);
                                window.location.href = '/dashboard';
                              } catch (err) {
                                console.error('Impersonation failed:', err);
                                alert('Failed to switch user. Check console for details.');
                              }
                            }}
                            disabled={u.user_id === realUser?.user_id}
                            title={u.user_id === realUser?.user_id ? 'Cannot view as yourself' : 'View as this user'}
                          >
                            <Eye className="inline" size={14} />
                          </button>
                          <button
                            className={`text-xs px-2 py-1 rounded ${u.user_id === realUser?.user_id ? 'bg-gray-400 opacity-50 cursor-not-allowed' : 'bg-rs-red/70 hover:bg-rs-red'}`}
                            onClick={() => { if (u.user_id !== realUser?.user_id) { setUserToDelete(u); setShowDeleteModal(true); } }}
                            disabled={u.user_id === realUser?.user_id}
                            title={u.user_id === realUser?.user_id ? 'Cannot delete yourself' : 'Delete'}
                          >
                            <Trash2 className="inline" size={14} />
                          </button>
                        </>
                      );
                      return (
                        <React.Fragment key={u.user_id}>
                          {isNewCompany && (() => {
                            const comp = getCompany(u);
                            const compUsers = users.filter(cu => cu.company_id === u.company_id);
                            const mostRecentLogin = compUsers.reduce<number | null>((acc, cu) => {
                              const v = cu.last_login ? (typeof cu.last_login === 'number' ? cu.last_login : Date.parse(cu.last_login as string) / 1000) : null;
                              return v && (!acc || v > acc) ? v : acc;
                            }, null);
                            const trialUsers = compUsers.filter(cu => cu.on_free_trial);
                            const trialEndMs = trialUsers.reduce<number | null>((acc, cu) => {
                              const ends = cu.free_trial_ends_at
                                ? Number(cu.free_trial_ends_at)
                                : (cu.free_trial_started_at && cu.free_trial_duration_days)
                                  ? Number(cu.free_trial_started_at) + Number(cu.free_trial_duration_days) * 86400
                                  : null;
                              if (!ends || Number.isNaN(ends)) return acc;
                              const ms = ends > 1e12 ? ends : ends * 1000;
                              return !acc || ms > acc ? ms : acc;
                            }, null);
                            const trialEndLabel = trialEndMs ? new Date(trialEndMs).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : null;
                            const subStatus = (comp?.subscription_status || '').toLowerCase();
                            const rawPeriodEnd = comp?.subscription_current_period_end;
                            const periodEnd = rawPeriodEnd ? (Number(rawPeriodEnd) > 1e12 ? new Date(Number(rawPeriodEnd)) : new Date(Number(rawPeriodEnd) * 1000)) : null;
                            const endLabel = periodEnd ? periodEnd.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : null;
                            const periodEndPassed = periodEnd ? periodEnd.getTime() <= Date.now() : false;
                            const membershipLabel = getMembershipName(comp?.membership_id);
                            const statusLabel = subStatus === 'paused'
                              ? `Paused${endLabel ? ` · ${periodEndPassed ? 'Expired' : 'Expires'} ${endLabel}` : ''}`
                              : subStatus === 'canceled'
                              ? `Canceled${endLabel ? ` · until ${endLabel}` : ''}`
                              : '';
                            const hasExpiredTrial = trialUsers.length > 0 && trialEndMs !== null && trialEndMs <= Date.now();
                            const isInactive = comp?.is_active === false;
                            const treatAsInactive = isInactive || hasExpiredTrial;
                            const inactiveSuffix = isInactive ? ' - Inactive' : hasExpiredTrial ? ' - Expired' : '';
                            const useLightText = subStatus === 'canceled' || treatAsInactive;
                            const rowText = useLightText ? 'text-rs-paper' : 'text-rs-ink';
                            const rowTextHover = useLightText ? 'hover:text-rs-paper/70' : 'hover:text-rs-ink/70';
                            const trialSuffix = (trialUsers.length > 0 && !hasExpiredTrial) ? ` - Trial${trialEndLabel ? ` · Ends ${trialEndLabel}` : ''}` : '';
                            const companyId = u.company_id as number;
                            const isCollapsed = collapsedCompanyIds.has(companyId);
                            return (
                              <tr
                                className={`border-b border-rs-jet cursor-pointer ${treatAsInactive ? 'bg-rs-red/50' : subStatus === 'paused' ? 'bg-amber-300' : subStatus === 'canceled' ? 'bg-rs-red' : 'bg-rs-dark-green'}`}
                                onClick={e => {
                                  if ((e.target as HTMLElement).closest('button')) return;
                                  setCollapsedCompanyIds(prev => {
                                    const next = new Set(prev);
                                    if (next.has(companyId)) next.delete(companyId); else next.add(companyId);
                                    return next;
                                  });
                                }}
                              >
                                {/* Name col */}
                                <td colSpan={2}>
                                  <div className="flex items-center gap-1.5">
                                    {isCollapsed ? <ChevronUp className={rowText} size={16} /> : <ChevronDown className={rowText} size={16} />}
                                    <div>
                                      <button
                                        className={`bg-transparent font-bold ${rowText} ${rowTextHover} tracking-wide text-base pb-0!`}
                                        onClick={() => setSelectedCompanyIds([companyId])}
                                      >
                                        {getCompanyName(u)}
                                      </button>
                                      <div className={`text-sm whitespace-nowrap pl-4 pb-2 ${rowText}`}>
                                        {membershipLabel}{statusLabel ? ` - ${statusLabel}` : ''}{trialSuffix}{inactiveSuffix}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                {/* Last Login col (hidden lg) */}
                                <td className={`hidden lg:table-cell px-3 py-2 text-center text-sm ${rowText}`}>
                                  {mostRecentLogin ? new Date(mostRecentLogin * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                                </td>
                                {/* Actions col (hidden md) */}
                                <td className="hidden md:table-cell px-3 py-2" />
                              </tr>
                            );
                          })()}
                          {!collapsedCompanyIds.has(u.company_id as number) && (
                          <tr
                            className={`border-b border-rs-jet/50 hover:bg-rs-jet/30 cursor-pointer ${getCompany(u)?.is_active === false ? 'bg-rs-red/10' : getCompany(u)?.subscription_status === 'paused' ? 'bg-amber-500/10' : getCompany(u)?.subscription_status === 'canceled' ? 'bg-rs-red/10' : ''}`}
                            onClick={e => {
                              if ((e.target as HTMLElement).closest('button')) return;
                              setDetailsUser(u);
                              setShowDetailsModal(true);
                            }}
                          >
                            <td className="pl-8 pr-3 py-2">
                              <div className="font-medium">{u.first_name} {u.last_name}</div>
                              <div className="text-xs text-rs-off-white/50 md:hidden">{u.email}</div>
                            </td>
                            <td className="px-3 py-2 text-rs-off-white/70 hidden md:table-cell">{u.email}</td>
                            <td className="px-3 py-2 text-xs text-center hidden lg:table-cell">{u.last_login ? (typeof u.last_login === 'number' ? new Date(u.last_login * 1000).toLocaleString() : new Date(Date.parse(u.last_login as string)).toLocaleString()) : 'Never'}</td>
                            <td className="px-3 py-2 hidden md:table-cell">
                              <div className="flex items-center gap-1.5 my-1 justify-center">
                                {actionButtons}
                              </div>
                            </td>
                          </tr>
                          )}
                          {!collapsedCompanyIds.has(u.company_id as number) && (
                          <tr className="md:hidden border-b border-rs-jet/50 bg-rs-jet/10">
                            <td colSpan={4} className="px-3 pb-2 pt-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {actionButtons}
                              </div>
                            </td>
                          </tr>
                          )}
                        </React.Fragment>
                      );
                    });
                  })()}
                  {paginatedUsers.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-8 text-center text-rs-off-white/40">
                        {userSearch || selectedCompanyIds.length > 0 ? 'No users match your filters' : 'No users found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-3">
                <span className="text-sm text-rs-off-white/60">Page {currentPage} of {totalPages}</span>
                <div className="flex gap-2">
                  <button className="px-2 py-1 rounded bg-rs-jet text-rs-off-white text-sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Prev</button>
                  <button className="px-2 py-1 rounded bg-rs-jet text-rs-off-white text-sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
                </div>
              </div>
            )}
          </div>
        )}

      {/* Add/Edit User Modal */}
      {showAddEditModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-rs-off-black rounded shadow-lg w-full max-w-xl max-h-screen flex flex-col">
            <div className="flex justify-between items-center mb-4 p-5 border-b border-rs-off-white/10">
              <h2 className="text-xl">{editingUser ? 'Edit User' : 'Add User'}</h2>
              <button className="text-rs-off-white" onClick={() => setShowAddEditModal(false)}>✕</button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {modalTab === 1 && (
                <form className="flex flex-col gap-5 p-5" data-hs-do-not-collect="true">
                  <div>
                    <label>First Name</label>
                    <input className="w-full bg-rs-jet text-rs-off-white p-2 rounded" name="first_name" value={userForm.first_name || ''} onChange={handleFormChange} />
                  </div>
                  <div>
                    <label>Last Name</label>
                    <input className="w-full bg-rs-jet text-rs-off-white p-2 rounded" name="last_name" value={userForm.last_name || ''} onChange={handleFormChange} />
                  </div>
                  <div>
                    <label>Email</label>
                    <input className="w-full bg-rs-jet text-rs-off-white p-2 rounded" name="email" value={userForm.email || ''} onChange={handleFormChange} />
                  </div>
                  {editingUser ? (
                    <div>
                      <label className="text-rs-off-white/60">Company</label>
                      <div className="w-full bg-rs-jet/40 text-rs-off-white/70 p-2 rounded border border-rs-jet-light/50 text-sm">
                        {getCompanyName(editingUser) || <span className="text-rs-off-white/30">No company assigned</span>}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label>Company <span className="text-xs text-rs-off-white/40">(optional — auto-detected from email domain)</span></label>
                      <select
                        className="w-full bg-rs-jet text-rs-off-white p-2 rounded"
                        name="company_id"
                        value={userForm.company_id || ''}
                        onChange={e => setUserForm((prev: any) => ({ ...prev, company_id: e.target.value ? Number(e.target.value) : undefined }))}
                      >
                        <option value="">Auto-detect from email</option>
                        {companies.filter(c => c.is_active).sort((a: any, b: any) => a.name.localeCompare(b.name)).map((c: any) => (
                          <option key={c.company_id} value={c.company_id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {editingUser && (
                    <div>
                      <label className="text-rs-off-white/60">Membership <span className="text-rs-off-white/30 text-xs ml-1">(inherited from company)</span></label>
                      <div className="w-full bg-rs-jet/40 text-rs-off-white/70 p-2 rounded border border-rs-jet-light/50 text-sm">
                        {getMembershipName(getInheritedMembershipId(editingUser)) || '—'}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="flex items-center gap-2">
                      Admin Rights
                      {editingUser && !isEmployee(editingUser) && (
                        <span className="text-xs text-rs-off-white/40">(only available for Employee members)</span>
                      )}
                    </label>
                    {editingUser && !isEmployee(editingUser) ? (
                      <div className="w-full bg-rs-jet/40 text-rs-off-white/40 p-2 rounded border border-rs-jet-light/50 text-sm">
                        {userForm.is_admin ? 'Yes' : 'No'} — not editable
                      </div>
                    ) : (
                      <select className="w-full bg-rs-jet text-rs-off-white p-2 rounded" name="is_admin" value={userForm.is_admin ? 'true' : 'false'} onChange={e => setUserForm((prev: any) => ({ ...prev, is_admin: e.target.value === 'true' }))}>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    )}
                  </div>
                  <div>
                    <label>Active Status</label>
                    <select className="w-full bg-rs-jet text-rs-off-white p-2 rounded" name="is_active" value={userForm.is_active ? 'true' : 'false'} onChange={e => setUserForm((prev: any) => ({ ...prev, is_active: e.target.value === 'true' }))}>
                      <option value="true">Active</option>
                      <option value="false">Deactivated</option>
                    </select>
                  </div>
                  {editingUser && (
                    <div className="mt-4 p-3 bg-rs-jet/50 rounded border border-rs-green/30">
                      <h3 className="text-sm font-semibold text-rs-green mb-2">Free Trial Settings</h3>
                      <div className="flex flex-col gap-3">
                        <div>
                          <label className="text-rs-off-white/70">On Free Trial</label>
                          <select
                            className="w-full bg-rs-jet text-rs-off-white p-2 rounded mt-1"
                            name="on_free_trial"
                            value={userForm.on_free_trial ? 'true' : 'false'}
                            onChange={e => setUserForm((prev: any) => ({ ...prev, on_free_trial: e.target.value === 'true' }))}
                          >
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        </div>
                        {userForm.on_free_trial && (
                          <div>
                            <label className="text-rs-off-white/70">Trial Start Date</label>
                            <input
                              type="date"
                              className="w-full bg-rs-jet text-rs-off-white p-2 rounded mt-1"
                              value={userForm.free_trial_started_at ? new Date(userForm.free_trial_started_at * 1000).toISOString().split('T')[0] : ''}
                              onChange={e => {
                                const ts = e.target.value ? Math.floor(new Date(e.target.value + 'T12:00:00Z').getTime() / 1000) : undefined;
                                setUserForm((prev: any) => ({ ...prev, free_trial_started_at: ts }));
                              }}
                            />
                          </div>
                        )}
                        <div>
                          <label className="text-rs-off-white/70">Trial Duration (days)</label>
                          <input
                            className="w-full bg-rs-jet text-rs-off-white p-2 rounded mt-1"
                            type="number"
                            name="free_trial_duration_days"
                            value={userForm.free_trial_duration_days || ''}
                            onChange={handleFormChange}
                            placeholder="e.g., 14"
                          />
                        </div>
                        {userForm.on_free_trial && userForm.free_trial_started_at && userForm.free_trial_duration_days && (
                          <div className="pt-2 border-t border-rs-off-white/20">
                            <label className="text-rs-off-white/70">Trial Ends</label>
                            <div className="text-rs-off-white text-sm mt-1">
                              {(() => {
                                const startDate = new Date(userForm.free_trial_started_at * 1000);
                                const endDate = new Date(startDate);
                                endDate.setDate(endDate.getDate() + userForm.free_trial_duration_days);
                                const today = new Date();
                                const diffTime = endDate.getTime() - today.getTime();
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                return (
                                  <>
                                    {endDate.toLocaleDateString()}
                                    <span className={`ml-2 text-xs ${diffDays < 7 ? 'text-rs-red' : 'text-rs-green'}`}>
                                      ({diffDays > 0 ? `${diffDays} days remaining` : 'Expired'})
                                    </span>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {modalError && <div className="text-red-500 text-sm">{modalError}</div>}
                </form>
              )}
            </div>

            <div className="flex gap-2 p-5 border-t border-rs-off-white/10 bg-rs-off-black/50">
              <button type="button" className="jet px-4 py-2 rounded" onClick={() => setShowAddEditModal(false)}>Cancel</button>
              {editingUser && (
                <>
                  <button type="button" className="bg-rs-blue-dash/70 hover:bg-rs-blue-dash text-sm px-4 py-2 rounded" onClick={openResetPasswordModal}>
                    <Mail className="inline w-4 h-4 mr-1" /> Reset Password
                  </button>
                  <button type="button" className="bg-amber-600/80 hover:bg-amber-500 text-sm px-4 py-2 rounded" onClick={() => { setShowAddEditModal(false); openPasswordModal(); }}>
                    <KeyRound className="inline w-4 h-4 mr-1" /> Set Password
                  </button>
                </>
              )}
              <button type="button" className="green px-4 py-2 rounded" onClick={handleSaveUser} disabled={saving}>
                <SaveIcon className="inline w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-rs-off-black p-8 rounded shadow-lg w-full max-w-md">
            <h2 className="text-xl mb-4">Change Password for {editingUser?.first_name} {editingUser?.last_name}</h2>
            <div className="relative mb-4">
              <input className="w-full bg-rs-jet text-rs-off-white p-2 pr-10 rounded" type={showPassword ? 'text' : 'password'} placeholder="New password" value={passwordValue} onChange={e => setPasswordValue(e.target.value)} />
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-rs-off-white/70 hover:text-rs-off-white" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <div className="relative mb-4">
              <input className="w-full bg-rs-jet text-rs-off-white p-2 pr-10 rounded" type={showPasswordConfirm ? 'text' : 'password'} placeholder="Confirm new password" value={passwordConfirmValue} onChange={e => setPasswordConfirmValue(e.target.value)} />
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-rs-off-white/70 hover:text-rs-off-white" onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}>
                {showPasswordConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {passwordError && <div className="text-red-500 text-sm mb-2">{passwordError}</div>}
            <div className="flex gap-2 mt-2">
              <button className="jet px-4 py-2 rounded" onClick={() => setShowPasswordModal(false)}>Cancel</button>
              <button className="green px-4 py-2 rounded" onClick={validateAndConfirmPasswordChange} disabled={passwordSaving}>Change Password</button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Confirmation Dialog */}
      {showPasswordConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60]">
          <div className="bg-rs-off-black p-8 rounded shadow-lg w-full max-w-md border border-rs-green/30">
            <h2 className="text-xl mb-4">Confirm Password Change</h2>
            <p className="text-sm text-rs-off-white/70 mb-4">Are you sure you want to change the password for <strong>{editingUser?.first_name} {editingUser?.last_name}</strong>?</p>
            <p className="text-xs text-rs-off-white/50 mb-4">This will immediately change their password and they will need to use the new password to log in.</p>
            {passwordError && <div className="text-red-500 text-sm mb-2">{passwordError}</div>}
            <div className="flex gap-2">
              <button className="border-1 border-rs-off-white/30 bg-rs-jet/35 hover:bg-rs-jet px-4 py-2 rounded" onClick={() => setShowPasswordConfirmDialog(false)} disabled={passwordSaving}>Cancel</button>
              <button className="bg-rs-green/50 hover:bg-rs-green hover:text-off-black text-rs-off-black px-4 py-2 rounded" onClick={handlePasswordChange} disabled={passwordSaving}>{passwordSaving ? 'Changing...' : 'Confirm Change'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Email Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-rs-off-black p-8 rounded shadow-lg w-full max-w-md">
            <h2 className="text-xl mb-4">Send Password Reset Email</h2>
            <p className="text-sm text-rs-off-white/70 mb-4">This will send a password reset email to <strong>{editingUser?.email}</strong>. The user will receive a link to create a new password.</p>
            <p className="text-xs text-rs-off-white/50 mb-4">The reset link will expire after 24 hours.</p>
            {resetPasswordError && <div className="text-red-500 text-sm mb-2">{resetPasswordError}</div>}
            {resetPasswordSuccess && (
              <div className="text-green-500 text-sm mb-2 p-3 bg-rs-green/10 rounded border border-rs-green/30">
                ✓ Password reset email sent successfully!
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <button className="jet px-4 py-2 rounded" onClick={() => { setShowResetPasswordModal(false); setResetPasswordSuccess(false); }}>{resetPasswordSuccess ? 'Close' : 'Cancel'}</button>
              {!resetPasswordSuccess && (
                <button className="green px-4 py-2 rounded" onClick={handleResetPassword} disabled={resetPasswordSending}>
                  <Mail className="inline w-4 h-4 mr-1" /> {resetPasswordSending ? 'Sending...' : 'Send Reset Email'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-rs-off-black p-8 rounded shadow-lg w-full max-w-md">
            <h2 className="text-xl mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to delete the user <strong>{userToDelete.email}</strong>? This action cannot be undone.</p>
            {deleteError && <div className="text-rs-red text-base mb-2">{deleteError}</div>}
            <div className="flex gap-2">
              <button className="border-1 border-rs-off-white/30 bg-rs-jet/35 hover:bg-rs-jet px-4 py-2 rounded" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="bg-rs-red/50 hover:bg-rs-red px-4 py-2 rounded" onClick={() => handleDeleteUser(userToDelete)} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && detailsUser && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-rs-off-black p-6 rounded-lg shadow-lg w-full max-w-lg border border-rs-jet-light/70">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl text-rs-off-white">User Details</h2>
              <button className="text-rs-off-white hover:text-rs-green text-2xl" onClick={() => setShowDetailsModal(false)}>✕</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 bg-rs-jet/30 p-4 rounded">
              <div className="flex justify-between items-center col-span-2 mb-0">
                <span className="text-rs-off-white text-2xl mr-1">{detailsUser.first_name} {detailsUser.last_name}</span>
                <div>
                  <span className="mr-1 text-rs-rb-gradient-end">Last login: </span>
                  <span className="text-rs-off-white/70">{detailsUser.last_login ? new Date(detailsUser.last_login * 1000).toLocaleString() : 'Never'}</span>
                </div>
              </div>
              <div className="flex justify-between items-center col-span-2 -mt-4">
                <span className="text-rs-off-white/45 text-lg">{getCompanyName(detailsUser)}</span>
              </div>
              <div className="flex justify-between items-center col-span-2 mb-5">
                <hr className="w-full border border-rs-off-white/50" />
              </div>
              <div className="flex flex-col">
                <span className="text-rs-green font-medium">Email</span>
                <span className="text-rs-off-white text-lg break-all">{detailsUser.email}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-rs-green font-medium">Admin Rights</span>
                <span className="text-rs-off-white text-lg">{detailsUser.is_admin ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-rs-green font-medium">Active Status</span>
                {(() => {
                  const company = getCompany(detailsUser);
                  const subStatus = ((company?.subscription_status || detailsUser.subscription_status) || '').toLowerCase();
                  const rawPeriodEnd = company?.subscription_current_period_end ?? detailsUser.subscription_current_period_end;
                  const periodEnd = rawPeriodEnd ? (Number(rawPeriodEnd) > 1e12 ? new Date(Number(rawPeriodEnd)) : new Date(Number(rawPeriodEnd) * 1000)) : null;
                  const endLabel = periodEnd
                    ? periodEnd.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                    : null;
                  if (!detailsUser.is_active) {
                    return <span className="text-rs-red text-lg">Deactivated</span>;
                  }
                  if (subStatus === 'paused') {
                    return <span className="text-rs-off-white text-lg">Paused{endLabel ? ` — active until ${endLabel}` : ''}</span>;
                  }
                  if (subStatus === 'canceled') {
                    return <span className="text-rs-off-white text-lg">Canceled{endLabel ? ` — active until ${endLabel}` : ''}</span>;
                  }
                  return <span className="text-rs-off-white text-lg">Active</span>;
                })()}
              </div>
              <div className="flex flex-col">
                <span className="text-rs-green font-medium">Membership <span className="text-rs-off-white/30 text-xs font-normal">(via company)</span></span>
                <span className="text-rs-off-white text-lg">{getMembershipName(getInheritedMembershipId(detailsUser))}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-rs-green font-medium">Company Role</span>
                <span className="text-rs-off-white text-lg capitalize">{detailsUser.company_role ?? '—'}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-rs-green font-medium">Free Trial</span>
                <span className="text-rs-off-white text-lg">{detailsUser.on_free_trial ? 'Yes' : 'No'}</span>
              </div>
              {detailsUser.free_trial_started_at && (
                <div className="flex flex-col">
                  <span className="text-rs-green font-medium">Trial Started</span>
                  <span className="text-rs-off-white text-lg">{new Date((detailsUser.free_trial_started_at as unknown as number) * 1000).toLocaleDateString()}</span>
                </div>
              )}
              {detailsUser.free_trial_duration_days && (
                <div className="flex flex-col">
                  <span className="text-rs-green font-medium">Trial Duration (days)</span>
                  <span className="text-rs-off-white text-lg">{detailsUser.free_trial_duration_days}</span>
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-rs-green font-medium">Created</span>
                <span className="text-rs-off-white text-lg">{detailsUser.created_at ? new Date((detailsUser.created_at as unknown as number) * 1000).toLocaleDateString() : ''}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-rs-green font-medium">Login Streak</span>
                {(() => {
                  const accountAgeWeeks = detailsUser.created_at ? Math.max(1, Math.floor((Date.now() - Number(detailsUser.created_at) * 1000) / (7 * 24 * 3600 * 1000)) + 1) : null;
                  const displayStreak = accountAgeWeeks ? Math.min(detailsUser.login_streak_counter ?? 0, accountAgeWeeks) : (detailsUser.login_streak_counter ?? 0);
                  return <span className="text-rs-off-white text-lg">{displayStreak} week{displayStreak !== 1 ? 's' : ''}</span>;
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
